import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import TableState from './store/table';
import LienAddressState from './store/lienAddress';
import WorkflowState from './store/workflow';
import CommonState from './store/common';

Vue.use(Vuex);

const APIBASEURL = process.env.VUE_APP_BACKEND_DOMAIN;

const paginationTitles = {
  dashboard: {
    liens: {
      pageNumber: 1,
      perPage: 20
    },
    letters: {
      pageNumber: 1,
      perPage: 40
    },
    ads: {
      pageNumber: 1,
      perPage: 10
    },
    approvals: {
      pageNumber: 1,
      perPage: 20
    },
    auctions: {
      pageNumber: 1,
      perPage: 20
    },
    liensOverdue: {
      pageNumber: 1,
      perPage: 20
    },
    tenantsEmail: {
      pageNumber: 1,
      perPage: 10
    },
    newspaperStep: {
      pageNumber: 1,
      perPage: 20
    },
    library: {
      pageNumber: 1,
      perPage: 20
    }
  },
  reports: {
    lienToAuctionAnalyze: {
      pageNumber: 1,
      perPage: 20
    },
    stateLaws: {
      pageNumber: 1,
      perPage: 20
    },
    tenantsInfo: {
      pageNumber: 1,
      perPage: 20
    },
    activeClients: {
      pageNumber: 1,
      perPage: 20
    },
    tenantsOnAuctionStep: {
      pageNumber: 1,
      perPage: 20
    },
    proofOfMailing: {
      pageNumber: 1,
      perPage: 20
    },
    deletedLiens: {
      pageNumber: 1,
      perPage: 20
    }
  }
};

const filters = {
  location: '',
  dashboard: {
    liens: {
      search: '',
      location: ''
    },
    letters: {
      location: ''
    },
    ads: {},
    approvals: {},
    auctions: {
      search: '',
      location: ''
    },
    liensOverdue: {
      search: '',
      location: ''
    },
    tenantsEmail: {},
    newspaperStep: {}
  },
  reports: {
    lienToAuctionAnalyze: {},
    stateLaws: {},
    tenantsInfo: {},
    activeClients: {},
    tenantsOnAuctionStep: {},
    proofOfMailing: {},
    deletedLiens: {}
  }
};

const store = new Vuex.Store({
  state: {
    accessToken: localStorage.getItem('accessToken') || '',
    refreshToken: localStorage.getItem('refreshToken') || '',
    expirationDate:
      localStorage.getItem('expirationDate') || new Date().getTime(),
    locationsList: JSON.parse(localStorage.getItem('locationsList')) || [],
    user: JSON.parse(localStorage.getItem('user')) || {},
    company: JSON.parse(localStorage.getItem('company')) || {
      uuid: 'dd88b129-eee1-42a5-a314-de938d62a059'
    },
    organizations: JSON.parse(localStorage.getItem('organizations')) || [],
    organization: JSON.parse(localStorage.getItem('organization')) || {},
    selectedOrganization:
      JSON.parse(localStorage.getItem('selectedOrganization')) || {},
    rights: JSON.parse(localStorage.getItem('rights')) || {},
    accountReady: JSON.parse(localStorage.getItem('accountReady')) || false,
    CLIENT_CREDENTIALS: Object.freeze({
      client_id: 'd1120831-357f-4168-965b-74d318b0d3c3',
      client_secret:
        '5a0eab45e36c14e6441fc943e908bdde82ee8fe2db573a7fa019837306680d67bf127d5e'
    }),
    API_URL: APIBASEURL,
    showLogout: false,
    organizationToEdit: null,
    tabs: JSON.parse(localStorage.getItem('tabs')) || {
      dashboard: '',
      reports: ''
    },
    pageNumbers:
      JSON.parse(localStorage.getItem('pageNumbers')) || paginationTitles,
    filters: JSON.parse(localStorage.getItem('filters')) || filters,
    dashboardFilters: JSON.parse(localStorage.getItem('dashboard_filters')) || '',
    permissions: JSON.parse(localStorage.getItem('permissions')) || ''
  },
  mutations: {
    setDashboardFilters(state, payload) {
      state.dashboardFilters = payload;
      localStorage.setItem('dashboard_filters', JSON.stringify(state.dashboardFilters));
    },
    setFilters(state, payload) {
      if (payload.key === 'location') {
        state.filters.location = payload.value;
        return;
      }
      state.filters = {
        ...state.filters,
        [payload.key]: { ...filters[payload.key], [payload.tabName]: { ...state.filters[payload.key][payload.tabName], ...payload.value } }
      };

      localStorage.setItem('filters', JSON.stringify(state.filters));
    },
    setPermissions(state, payload) {
      state.permissions = payload;
      localStorage.setItem('permissions', JSON.stringify(state.permissions));
    },
    login(state, payload) {
      state.accessToken = payload.result.access_token;
      localStorage.setItem('accessToken', state.accessToken);
      state.refreshToken = payload.result.refresh_token;
      localStorage.setItem('refreshToken', state.refreshToken);
      state.user = payload.result.user;
      localStorage.setItem('user', JSON.stringify(state.user));

      if (state.user.access) {
        state.accountReady = true;
        localStorage.setItem('accountReady', state.accountReady);
      }

      const dt = new Date();
      // arbitrary 10sec delay to be sure expiration date is a little bit before the real one
      dt.setSeconds(dt.getSeconds() + Number(payload.result.expires_in) - 10);
      state.expirationDate = dt.getTime();
      localStorage.setItem('expirationDate', dt.getTime());
    },
    setSelectedTab(state, payload) {
      state.tabs = { ...state.tabs, ...payload };
      localStorage.setItem('tabs', JSON.stringify(state.tabs));
    },
    setPageNumber(state, payload) {
      state.pageNumbers = {
        ...state.pageNumbers,
        [payload.key]: {
          ...paginationTitles[payload.key],
          [payload.tabName]: {
            ...state.pageNumbers[payload.key][payload.tabName],
            ...payload.value
          }
        }
      };
      localStorage.setItem('pageNumbers', JSON.stringify(state.pageNumbers));
    },
    setLocationsList(state, payload) {
      state.locationsList = payload.result;
      localStorage.setItem('locationsList', JSON.stringify(payload.result));

      let canManage = 0;
      state.locationsList.forEach(
        (company) => { canManage += Number(company.canManage); }
      );
      state.user.is_manager = canManage > 0;
      localStorage.setItem('user', JSON.stringify(state.user));
    },
    setOrganizations(state, payload) {
      state.organizations = payload.result;
      localStorage.setItem('organizations', JSON.stringify(payload.result));
    },
    setSelectedOrganization(state, payload) {
      state.selectedOrganization = payload.organization;
      localStorage.setItem(
        'selectedOrganization',
        JSON.stringify(payload.organization)
      );
    },
    setOrganization(state, payload) {
      state.organization = payload.organization;
      localStorage.setItem(
        'organization',
        JSON.stringify(payload.organization)
      );
    },
    setOrganizationToEdit(state, payload) {
      state.organizationToEdit = payload.result;
    },
    setEmployeeData(state, payload) {
      state.company = payload.result.company;
      localStorage.setItem('company', JSON.stringify(payload.result.company));
      state.rights.can_view = payload.result.can_view;
      state.rights.can_send = payload.result.can_send;
      state.rights.can_manage = payload.result.can_manage;
      localStorage.setItem('rights', JSON.stringify(state.rights));
    },
    setAccountReady(state, accountReady) {
      state.accountReady = accountReady;
      localStorage.setItem('accountReady', JSON.stringify(state.accountReady));
    },
    logout(state) {
      state.accessToken = '';
      state.refreshToken = '';
      state.expirationDate = new Date().getTime();
      state.locationsList = [];
      state.user = {};
      state.company = {};
      state.rights = {};
      state.organizations = [];
      state.organization = {};
      state.selectedOrganization = {};
      state.showLogout = false;
      state.organizationToEdit = null;
      state.permissions = [];
      localStorage.clear();
    },
    refreshToken(state, payload) {
      state.accessToken = payload.data.result.access_token;
      localStorage.setItem('accessToken', state.accessToken);
      state.refreshToken = payload.data.result.refresh_token;
      localStorage.setItem('refreshToken', state.refreshToken);
      const dt = new Date();
      // arbitrary 10sec delay to be sure expiration date is a little bit before the real one
      dt.setSeconds(
        dt.getSeconds() + Number(payload.data.result.expires_in) - 10
      );
      state.expirationDate = dt.getTime();
      localStorage.setItem('expirationDate', state.expirationDate);
    },
    updateCompany(state, payload) {
      state.company = payload.data.result.company;
      localStorage.setItem('company', JSON.stringify(state.company));
    },
    updateUser(state, payload) {
      state.user = payload;
      localStorage.setItem('user', JSON.stringify(state.user));
    },
    setShouldLogout(state, logout) {
      state.showLogout = logout;
    }
  },
  actions: {
    logout(context, callback) {
      context.commit('logout');
      callback();
    },
    async getExcelFiles({ state }, { data, url }) {
      try {
        const response = await axios.post(state.API_URL + url, data, {
          headers: {
            Authorization: 'Bearer '.concat(state.accessToken),
            'Content-Type': 'application/json'
          },
          responseType: 'blob'
        });

        const blob = new Blob([response.data], { type: 'application/vnd.ms-excel' });
        return response.status === 200 ? blob : Promise.reject();
      } catch (error) {
        console.error('[Excel Files Error]:', error);
        return Promise.reject(error.message);
      }
    },
    async uploadExcelFile({ state }, { data, url }) {
      try {
        const response = await axios.post(state.API_URL + url, data, {
          headers: {
            Authorization: 'Bearer '.concat(state.accessToken),
            'Content-Type': 'application/json'
          }
        });
        return response.status === 200 ? response.data : Promise.reject();
      } catch (error) {
        console.error('[Excel Files Error]:', error);
        return Promise.reject(error.message);
      }
    },
    async getExcelFileByGetMethod({ state }, { url }) {
      try {
        const response = await axios.get(state.API_URL + url, {
          headers: {
            Authorization: 'Bearer '.concat(state.accessToken)
          },
          responseType: 'blob'
        });
        return response.status === 200 ? response.data : Promise.reject();
      } catch (error) {
        console.error('[Excel Files Error]:', error);
        return Promise.reject(error.message);
      }
    }
  },
  getters: {
    getDashboardFilters: (state) => state.dashboardFilters

  },
  modules: {
    TableState,
    LienAddressState,
    workflow: WorkflowState,
    common: CommonState
  }
});

export default store;
